import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  MagnifyingGlass, Radio, ArrowsClockwise, Broadcast, Trash, Heart,
  ClockCounterClockwise, Globe, List, X,
} from "@phosphor-icons/react";
import StationRow from "@/components/StationRow";
import PlayerBar from "@/components/PlayerBar";
import BottomNav from "@/components/BottomNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TAGS = [
  "news", "talk", "pop", "rock", "classical", "jazz", "dance",
  "schlager", "kultur", "nachrichten", "electronic", "indie", "80s", "90s",
];

const INTERVAL_OPTIONS = [
  { value: 8, label: "8s" },
  { value: 12, label: "12s" },
  { value: 20, label: "20s" },
];

// Timing constants
const STATIONS_REQUEST_TIMEOUT_MS = 15000;       // /api/stations call deadline
const STATIONS_RETRY_BASE_DELAY_MS = 600;        // backoff between station-list retries
const STATIONS_MAX_ATTEMPTS = 3;                 // total attempts (initial + 2 retries)
const SEARCH_DEBOUNCE_MS = 280;                  // search-input debounce
const TRANSCRIBE_REQUEST_TIMEOUT_MS = 60000;     // /api/transcribe call deadline
const TRANSCRIBE_ERROR_TOAST_COOLDOWN_MS = 30000; // suppress repeat error toasts

function classNames(...a) { return a.filter(Boolean).join(" "); }

function uid() { return Math.random().toString(36).slice(2) + Date.now().toString(36); }

function formatTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return ""; }
}

export default function RadioApp() {
  const [tab, setTab] = useState("all"); // all | favorites | history
  const [query, setQuery] = useState("");
  const [activeTag, setActiveTag] = useState(null);
  const [stations, setStations] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loadingStations, setLoadingStations] = useState(false);
  const [stationsError, setStationsError] = useState(null);

  const [currentStation, setCurrentStation] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.85);
  const [muted, setMuted] = useState(false);

  const [transcripts, setTranscripts] = useState([]); // {id, german, english, timestamp, ...}
  const [isTranscribing, setIsTranscribing] = useState(true);
  const [inFlight, setInFlight] = useState(false);
  const [interval, setIntervalSec] = useState(12);
  const [sessionId] = useState(uid());

  const [history, setHistory] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile drawer
  const [mobileView, setMobileView] = useState("channels"); // channels | nowplaying | favorites | settings
  const [monetagLoaded, setMonetagLoaded] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false); // mobile settings sheet

  const audioRef = useRef(null);
  const intervalRef = useRef(null);
  const inFlightRef = useRef(false);
  const playingRef = useRef(false);
  const transcribingRef = useRef(true);
  const currentStationRef = useRef(null);
  const transcriptsBoxRef = useRef(null);

  // Load favorites
  const loadFavorites = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/favorites`);
      setFavorites(r.data || []);
    } catch (e) {
      console.error("loadFavorites failed:", e);
    }
  }, []);

  // Load stations — with auto-retry (2 attempts) + inline error state
  const loadStations = useCallback(async () => {
    setLoadingStations(true);
    setStationsError(null);
    const params = { limit: 80 };
    if (query.trim()) params.q = query.trim();
    if (activeTag) params.tag = activeTag;

    const attempt = async () => {
      const r = await axios.get(`${API}/stations`, { params, timeout: STATIONS_REQUEST_TIMEOUT_MS });
      return r.data || [];
    };

    let lastErr = null;
    for (let i = 0; i < STATIONS_MAX_ATTEMPTS; i++) {
      try {
        const data = await attempt();
        setStations(data);
        setStationsError(null);
        setLoadingStations(false);
        return;
      } catch (e) {
        lastErr = e;
        if (i < STATIONS_MAX_ATTEMPTS - 1) {
          await new Promise(res => setTimeout(res, STATIONS_RETRY_BASE_DELAY_MS * (i + 1)));
        }
      }
    }
    console.error(`loadStations failed after ${STATIONS_MAX_ATTEMPTS} attempts:`, lastErr);
    const status = lastErr?.response?.status;
    const msg = status
      ? `Server antwortet nicht (HTTP ${status}). Bitte erneut versuchen.`
      : "Verbindung fehlgeschlagen. Bitte erneut versuchen.";
    setStationsError(msg);
    setLoadingStations(false);
  }, [query, activeTag]);

  useEffect(() => { loadFavorites(); }, [loadFavorites]);

  useEffect(() => {
    const t = setTimeout(loadStations, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [loadStations]);

  // Sync volume to audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = muted ? 0 : volume;
    }
  }, [volume, muted]);

  const isFav = useCallback((uuid) => favorites.some(f => f.stationuuid === uuid), [favorites]);

  const toggleFavorite = useCallback(async (st) => {
    try {
      if (isFav(st.stationuuid)) {
        await axios.delete(`${API}/favorites/${st.stationuuid}`);
        setFavorites(prev => prev.filter(f => f.stationuuid !== st.stationuuid));
        toast("Removed from favorites");
      } else {
        const r = await axios.post(`${API}/favorites`, {
          stationuuid: st.stationuuid,
          name: st.name,
          url: st.url_resolved || st.url,
          favicon: st.favicon || "",
          tags: st.tags || "",
          bitrate: st.bitrate || 0,
        });
        setFavorites(prev => [r.data, ...prev.filter(f => f.stationuuid !== st.stationuuid)]);
        toast.success("Added to favorites");
      }
    } catch (e) {
      console.error("toggleFavorite failed:", e);
      toast.error("Favorite action failed");
    }
  }, [isFav]);

  // Fetch one transcript chunk
  const fetchTranscript = useCallback(async () => {
    const station = currentStationRef.current;
    if (!station || inFlightRef.current) return;
    inFlightRef.current = true;
    setInFlight(true);
    try {
      const r = await axios.post(`${API}/transcribe`, {
        stream_url: station.url_resolved || station.url,
        station_name: station.name,
        stationuuid: station.stationuuid,
        session_id: sessionId,
        duration: 8,
      }, { timeout: TRANSCRIBE_REQUEST_TIMEOUT_MS });
      const chunk = r.data;
      if (chunk && (chunk.german || chunk.english)) {
        setTranscripts(prev => [...prev, chunk]);
      }
    } catch (e) {
      console.error("fetchTranscript failed:", e);
      // Surface a toast on first failure, then suppress for a cooldown window.
      if (!fetchTranscript._errored) {
        fetchTranscript._errored = true;
        toast.error("Live transcription temporarily unavailable");
        setTimeout(() => { fetchTranscript._errored = false; }, TRANSCRIBE_ERROR_TOAST_COOLDOWN_MS);
      }
    } finally {
      inFlightRef.current = false;
      setInFlight(false);
    }
  }, [sessionId]);

  // Manage transcribe loop
  useEffect(() => {
    playingRef.current = isPlaying;
    transcribingRef.current = isTranscribing;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (isPlaying && isTranscribing && currentStation) {
      // Kick off first chunk immediately
      fetchTranscript();
      intervalRef.current = setInterval(() => {
        if (playingRef.current && transcribingRef.current) fetchTranscript();
      }, interval * 1000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying, isTranscribing, currentStation, interval, fetchTranscript]);

  // Auto-scroll transcripts
  useEffect(() => {
    const el = transcriptsBoxRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [transcripts]);

  const playStation = useCallback((station) => {
    setCurrentStation(station);
    currentStationRef.current = station;
    setTranscripts([]); // fresh transcript on station change
    setSidebarOpen(false); // auto-close mobile drawer
    setMobileView("nowplaying"); // switch to Now Playing on mobile
    setTimeout(() => {
      const a = audioRef.current;
      if (!a) return;
      a.src = station.url_resolved || station.url;
      a.volume = muted ? 0 : volume;
      a.play().then(() => setIsPlaying(true)).catch(() => {
        toast.error("Could not play this stream. Try another station.");
        setIsPlaying(false);
      });
    }, 20);
  }, [volume, muted]);

  const togglePlay = useCallback(() => {
    const a = audioRef.current;
    if (!a || !currentStation) return;
    if (a.paused) {
      // Re-set src to force fresh connection (live streams)
      a.src = currentStation.url_resolved || currentStation.url;
      a.play().then(() => setIsPlaying(true)).catch(() => toast.error("Playback failed"));
    } else {
      a.pause();
      setIsPlaying(false);
    }
  }, [currentStation]);

  const onAudioPlay = () => setIsPlaying(true);
  const onAudioPause = () => setIsPlaying(false);
  const onAudioError = () => {
    if (currentStation) toast.error("Stream error — station may be offline");
    setIsPlaying(false);
  };

  const visibleStations = useMemo(() => {
    if (tab === "favorites") {
      return favorites.map(f => ({
        stationuuid: f.stationuuid, name: f.name, url: f.url, url_resolved: f.url,
        favicon: f.favicon, tags: f.tags, bitrate: f.bitrate,
      }));
    }
    return stations;
  }, [tab, stations, favorites]);

  const loadHistory = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/transcripts`, { params: { limit: 200 } });
      setHistory(r.data || []);
    } catch (e) {
      console.error("loadHistory failed:", e);
    }
  }, []);

  useEffect(() => {
    if (tab === "history") loadHistory();
  }, [tab, loadHistory]);

  // MONETAG LAZY LOADER — inject ad scripts only 20s AFTER the user has picked
  // their first station. Guarantees the initial navigation flow (hamburger,
  // station list taps, first play button) is 100% free of popunder hijacking.
  useEffect(() => {
    if (!currentStation || monetagLoaded) return;
    const t = setTimeout(() => {
      ["265467", "266355"].forEach((zone) => {
        if (document.querySelector(`script[data-zone="${zone}"]`)) return;
        const s = document.createElement("script");
        s.src = "https://quge5.com/88/tag.min.js";
        s.async = true;
        s.setAttribute("data-cfasync", "false");
        s.dataset.zone = zone;
        document.head.appendChild(s);
      });
      setMonetagLoaded(true);
    }, 20000);
    return () => clearTimeout(t);
  }, [currentStation, monetagLoaded]);

  const clearTranscripts = () => setTranscripts([]);

  return (
    <div className="h-screen w-screen flex flex-col bg-white text-[#09090B]" data-testid="radio-app-root">
      {/* TOP HEADER STRIP */}
      <header className="h-14 border-b border-[#18181B] flex items-center px-4 md:px-6 shrink-0">
        {/* Mobile hamburger */}
        <button
          onClick={(e) => { e.stopPropagation(); setSidebarOpen(true); }}
          data-testid="open-sidebar-btn"
          className="md:hidden mr-3 -ml-1 w-11 h-11 flex items-center justify-center hover:bg-[#F4F4F5] border border-[#E4E4E7]"
          aria-label="Open station list"
        >
          <List size={22} weight="bold" />
        </button>
        <div className="flex items-center gap-3">
          <Broadcast size={22} weight="duotone" />
          <div className="font-display text-lg sm:text-xl font-extrabold tracking-tighter leading-none">
            DEUTSCHES <span className="text-[#FF2A00]">RADIO</span>
          </div>
          <div className="overline ml-4 hidden md:block">Live Transkription · DE → EN</div>
        </div>
        <div className="ml-auto flex items-center gap-4">
          <span className="overline hidden md:block">Built by Rajib Bhattacharya</span>
          <span className="overline hidden lg:block text-[#A1A1AA]">·</span>
          <span className="overline hidden lg:block">Whisper · GPT-4o</span>
          <a
            href="https://www.radio-browser.info"
            target="_blank" rel="noreferrer"
            className="overline hover:text-[#002FA7] hidden sm:flex items-center gap-1"
          ><Globe size={12} /> RADIO-BROWSER</a>
        </div>
      </header>

      {/* MAIN */}
      <div className="flex-1 flex min-h-0 relative">
        {/* Mobile sidebar backdrop */}
        {sidebarOpen && (
          <div
            onClick={() => setSidebarOpen(false)}
            className="md:hidden fixed inset-0 top-14 bg-black/40 z-30"
            data-testid="sidebar-backdrop"
          />
        )}

        {/* SIDEBAR */}
        <aside
          className={classNames(
            "shrink-0 border-r border-[#E4E4E7] flex flex-col min-h-0 bg-white",
            // mobile: slide-in drawer (above player+bottom nav)
            "fixed md:static top-14 bottom-[152px] left-0 z-40 w-80 max-w-[85vw] md:max-w-none transition-transform md:transition-none",
            "md:top-auto md:bottom-auto md:translate-x-0 md:w-80",
            sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
          )}
          data-testid="sidebar"
        >
          {/* Mobile close header */}
          <div className="md:hidden flex items-center justify-between px-4 h-12 border-b border-[#E4E4E7]">
            <span className="overline">SENDER</span>
            <button
              onClick={(e) => { e.stopPropagation(); setSidebarOpen(false); }}
              data-testid="close-sidebar-btn"
              className="w-11 h-11 flex items-center justify-center hover:bg-[#F4F4F5]"
              aria-label="Close station list"
            >
              <X size={20} weight="bold" />
            </button>
          </div>
          {/* Search */}
          <div className="p-4 border-b border-[#E4E4E7]">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Sender suchen..."
                data-testid="station-search-input"
                className="w-full pl-9 pr-3 py-2.5 bg-white border border-[#18181B] rounded-none text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:ring-offset-1 placeholder:text-[#A1A1AA]"
              />
            </div>
            {/* Tabs */}
            <div className="mt-3 grid grid-cols-3 gap-0 border border-[#E4E4E7]">
              {[
                { k: "all", label: "ALLE", icon: Radio },
                { k: "favorites", label: "FAVORITEN", icon: Heart },
                { k: "history", label: "VERLAUF", icon: ClockCounterClockwise },
              ].map(({ k, label, icon: Icon }) => (
                <button
                  key={k}
                  onClick={() => setTab(k)}
                  data-testid={`tab-${k}-btn`}
                  className={classNames(
                    "py-2 text-[0.65rem] font-mono-tech tracking-[0.15em] flex items-center justify-center gap-1 transition-colors",
                    tab === k ? "bg-[#18181B] text-white" : "bg-white text-[#52525B] hover:bg-[#F4F4F5]"
                  )}
                >
                  <Icon size={11} weight="bold" /> {label}
                </button>
              ))}
            </div>
          </div>

          {/* Tag chips (only in 'all') */}
          {tab === "all" && (
            <div className="px-4 py-3 border-b border-[#E4E4E7] flex flex-wrap gap-1.5" data-testid="tag-list">
              <button
                onClick={() => setActiveTag(null)}
                className={classNames(
                  "px-2.5 py-1 text-[0.68rem] font-mono-tech uppercase tracking-wider border",
                  activeTag === null ? "border-[#18181B] bg-[#18181B] text-white" : "border-[#E4E4E7] text-[#52525B] hover:border-[#18181B]"
                )}
              >ALLE</button>
              {TAGS.map(t => (
                <button
                  key={t}
                  onClick={() => setActiveTag(activeTag === t ? null : t)}
                  data-testid={`tag-${t}`}
                  className={classNames(
                    "px-2.5 py-1 text-[0.68rem] font-mono-tech uppercase tracking-wider border",
                    activeTag === t ? "border-[#18181B] bg-[#18181B] text-white" : "border-[#E4E4E7] text-[#52525B] hover:border-[#18181B]"
                  )}
                >{t}</button>
              ))}
            </div>
          )}

          {/* Station list */}
          <div className="flex-1 overflow-y-auto thin-scroll" data-testid="station-list">
            {tab === "history" ? (
              <HistoryList history={history} onReload={loadHistory} />
            ) : loadingStations ? (
              <div className="p-6 flex items-center gap-2 overline" data-testid="stations-loading">
                <ArrowsClockwise size={12} className="animate-spin" /> Lade Sender…
              </div>
            ) : stationsError && tab === "all" ? (
              <div className="p-6 border-b border-[#E4E4E7]" data-testid="stations-error">
                <div className="overline mb-2 text-[#FF2A00]">⚠ FEHLER</div>
                <div className="text-sm text-[#09090B] leading-relaxed mb-4">
                  {stationsError}
                </div>
                <button
                  onClick={loadStations}
                  data-testid="retry-stations-btn"
                  className="w-full flex items-center justify-center gap-2 px-3 py-2.5 border border-[#18181B] bg-[#18181B] text-white text-[0.7rem] font-mono-tech tracking-[0.15em] hover:bg-[#FF2A00] hover:border-[#FF2A00] transition-colors"
                >
                  <ArrowsClockwise size={12} weight="bold" /> ERNEUT VERSUCHEN
                </button>
                <div className="overline mt-3 text-[#A1A1AA] text-[0.6rem]">
                  Auto-Retry bereits 3× erfolglos.
                </div>
              </div>
            ) : visibleStations.length === 0 ? (
              <div className="p-6">
                <div className="overline mb-2">Keine Ergebnisse</div>
                <div className="text-sm text-[#52525B]">
                  {tab === "favorites" ? "Du hast noch keine Favoriten." : "Versuch eine andere Suche."}
                </div>
              </div>
            ) : (
              visibleStations.map((st) => (
                <StationRow
                  key={st.stationuuid}
                  station={st}
                  active={currentStation?.stationuuid === st.stationuuid}
                  isPlayingThis={isPlaying && currentStation?.stationuuid === st.stationuuid}
                  fav={isFav(st.stationuuid)}
                  onPlay={() => playStation(st)}
                  onFav={() => toggleFavorite(st)}
                />
              ))
            )}
          </div>
        </aside>

        {/* CENTER — Transcript columns */}
        <main className="flex-1 flex flex-col min-w-0 min-h-0">
          {/* Section header */}
          <div className="h-14 border-b border-[#E4E4E7] flex items-center px-4 md:px-6 gap-3 md:gap-6 shrink-0 overflow-x-auto thin-scroll">
            <div className="flex items-center gap-2 shrink-0">
              {isPlaying ? <span className="live-dot" /> : <span className="w-2 h-2 rounded-full bg-[#A1A1AA] inline-block" />}
              <span className={classNames("font-mono-tech text-[0.7rem] tracking-[0.2em] font-semibold", isPlaying ? "text-[#FF2A00]" : "text-[#A1A1AA]")}>
                {isPlaying ? "LIVE" : "OFFLINE"}
              </span>
            </div>
            <div className="overline hidden sm:block">Transkript</div>
            <div className="ml-auto flex items-center gap-2 md:gap-3 shrink-0">
              <label className="overline hidden md:block">INTERVALL</label>
              <div className="flex border border-[#E4E4E7]">
                {INTERVAL_OPTIONS.map(o => (
                  <button
                    key={o.value}
                    onClick={() => setIntervalSec(o.value)}
                    data-testid={`interval-${o.value}s-btn`}
                    className={classNames(
                      "px-2 py-1 text-[0.7rem] font-mono-tech",
                      interval === o.value ? "bg-[#18181B] text-white" : "hover:bg-[#F4F4F5]"
                    )}
                  >{o.label}</button>
                ))}
              </div>
              <button
                onClick={() => setIsTranscribing(v => !v)}
                data-testid="toggle-transcribe-btn"
                className={classNames(
                  "px-2 md:px-3 py-1.5 text-[0.65rem] md:text-[0.7rem] font-mono-tech tracking-[0.15em] border whitespace-nowrap",
                  isTranscribing ? "border-[#18181B] bg-[#18181B] text-white" : "border-[#E4E4E7] text-[#52525B] hover:border-[#18181B]"
                )}
              >{isTranscribing ? "PAUSE" : "RESUME"}</button>
              <button
                onClick={clearTranscripts}
                data-testid="clear-transcripts-btn"
                className="p-1.5 border border-[#E4E4E7] hover:border-[#18181B]"
                title="Transkript löschen"
              ><Trash size={14} /></button>
            </div>
          </div>

          {/* Column headers */}
          <div className="grid grid-cols-1 md:grid-cols-2 border-b border-[#E4E4E7] shrink-0">
            <div className="px-4 md:px-6 py-2 md:border-r border-[#E4E4E7] flex items-center justify-between">
              <div className="font-display font-extrabold tracking-tight text-sm">DEUTSCH</div>
              <div className="overline">de · whisper-1</div>
            </div>
            <div className="px-4 md:px-6 py-2 flex items-center justify-between border-t md:border-t-0 border-[#E4E4E7] bg-[#FAFAFA] md:bg-transparent">
              <div className="font-display font-extrabold tracking-tight text-sm text-[#002FA7]">ENGLISH</div>
              <div className="overline">en · gpt-4o-mini</div>
            </div>
          </div>

          {/* Transcript body */}
          <div ref={transcriptsBoxRef} className="flex-1 overflow-y-auto thin-scroll min-h-0" data-testid="transcripts-box">
            {!currentStation ? (
              <EmptyState />
            ) : transcripts.length === 0 ? (
              <div className="h-full flex items-center justify-center p-8">
                <div className="text-center">
                  <div className="overline mb-3">Warte auf erstes Transkript…</div>
                  <div className="text-sm text-[#52525B] max-w-md">
                    Es werden alle {interval} Sekunden ~8s Audio aufgenommen, an Whisper geschickt und ins Englische übersetzt.
                  </div>
                </div>
              </div>
            ) : (
              transcripts.map((c, i) => (
                <div key={c.id || i} className="grid grid-cols-1 md:grid-cols-2 border-b border-[#E4E4E7] chunk-in" data-testid={`transcript-row-${i}`}>
                  <div className="px-4 md:px-6 py-3 md:py-4 md:border-r border-[#E4E4E7]">
                    <div className="overline mb-2">{formatTime(c.timestamp)} · DE</div>
                    <div className="text-[15px] leading-relaxed">{c.german || <span className="italic text-[#A1A1AA]">— Stille —</span>}</div>
                  </div>
                  <div className="px-4 md:px-6 py-3 md:py-4 bg-[#FAFAFA] border-t md:border-t-0 border-[#E4E4E7]">
                    <div className="overline mb-2 text-[#002FA7]">EN</div>
                    <div className="text-[15px] leading-relaxed text-[#09090B]">
                      {c.english || <span className="italic text-[#A1A1AA]">— silence —</span>}
                    </div>
                  </div>
                </div>
              ))
            )}
            {inFlight && currentStation && (
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="px-4 md:px-6 py-3 md:border-r border-[#E4E4E7] flex items-center gap-2 text-[#52525B]">
                  <ArrowsClockwise size={14} className="animate-spin" />
                  <span className="overline">Höre zu…</span>
                </div>
                <div className="px-6 py-3 hidden md:block" />
              </div>
            )}
          </div>
        </main>
      </div>

      {/* BOTTOM PLAYER BAR */}
      <PlayerBar
        station={currentStation}
        isPlaying={isPlaying}
        onToggle={togglePlay}
        volume={volume}
        muted={muted}
        onVolume={setVolume}
        onMute={() => setMuted(m => !m)}
      />

      {/* MOBILE BOTTOM NAVIGATION */}
      <BottomNav
        active={mobileView}
        onChange={(view) => {
          setMobileView(view);
          if (view === "channels") {
            setTab("all");
            setSidebarOpen(true);
            setSettingsOpen(false);
          } else if (view === "favorites") {
            setTab("favorites");
            setSidebarOpen(true);
            setSettingsOpen(false);
          } else if (view === "nowplaying") {
            setSidebarOpen(false);
            setSettingsOpen(false);
          } else if (view === "settings") {
            setSidebarOpen(false);
            setSettingsOpen(true);
          }
        }}
      />

      {/* MOBILE SETTINGS SHEET */}
      {settingsOpen && (
        <>
          <div
            onClick={() => { setSettingsOpen(false); setMobileView("nowplaying"); }}
            className="md:hidden fixed inset-0 bg-black/40 z-50"
            data-testid="settings-backdrop"
          />
          <div
            className="md:hidden fixed left-0 right-0 bottom-0 z-[60] bg-white border-t-2 border-[#18181B]"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
            data-testid="settings-sheet"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E4E4E7]">
              <span className="font-display font-extrabold tracking-tight text-base">EINSTELLUNGEN</span>
              <button
                onClick={() => { setSettingsOpen(false); setMobileView("nowplaying"); }}
                data-testid="close-settings-btn"
                className="w-11 h-11 flex items-center justify-center hover:bg-[#F4F4F5]"
                aria-label="Close settings"
              >
                <X size={20} weight="bold" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <div className="overline mb-2">Transkriptions-Intervall</div>
                <div className="grid grid-cols-3 gap-2">
                  {INTERVAL_OPTIONS.map((o) => (
                    <button
                      key={o.value}
                      onClick={() => setIntervalSec(o.value)}
                      data-testid={`settings-interval-${o.value}s`}
                      className={classNames(
                        "min-h-[44px] font-mono-tech text-sm border transition-colors",
                        interval === o.value
                          ? "bg-[#18181B] text-white border-[#18181B]"
                          : "border-[#E4E4E7] hover:border-[#18181B]"
                      )}
                    >{o.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="overline mb-2">Live-Transkription</div>
                <button
                  onClick={() => setIsTranscribing(v => !v)}
                  data-testid="settings-toggle-transcribe"
                  className={classNames(
                    "w-full min-h-[44px] font-mono-tech text-sm tracking-[0.15em] border",
                    isTranscribing
                      ? "border-[#18181B] bg-[#18181B] text-white"
                      : "border-[#E4E4E7] text-[#52525B] hover:border-[#18181B]"
                  )}
                >{isTranscribing ? "AKTIV · TIPPEN ZUM PAUSIEREN" : "PAUSIERT · TIPPEN ZUM STARTEN"}</button>
              </div>
              <div>
                <div className="overline mb-2">Transkript</div>
                <button
                  onClick={() => { clearTranscripts(); }}
                  data-testid="settings-clear-transcripts"
                  className="w-full min-h-[44px] border border-[#E4E4E7] text-[#09090B] hover:border-[#FF2A00] hover:text-[#FF2A00] flex items-center justify-center gap-2 text-sm font-mono-tech"
                >
                  <Trash size={16} /> Transkript löschen
                </button>
              </div>
              <div className="pt-4 border-t border-[#E4E4E7] text-[0.7rem] font-mono-tech text-[#A1A1AA] leading-relaxed">
                Built by Rajib Bhattacharya · Whisper · GPT-4o<br />
                Radio-Browser API
              </div>
            </div>
          </div>
        </>
      )}

      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        onPlay={onAudioPlay}
        onPause={onAudioPause}
        onError={onAudioError}
        crossOrigin="anonymous"
        preload="none"
        data-testid="audio-element"
      />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="h-full w-full grid-bg flex items-center justify-center">
      <div className="bg-white border border-[#18181B] p-10 max-w-lg text-center">
        <div className="overline mb-3">EMPTY · KEIN SIGNAL</div>
        <div className="font-display font-extrabold text-3xl tracking-tighter mb-4 leading-none">
          Wähle einen<br />
          <span className="text-[#FF2A00]">deutschen Sender</span>
        </div>
        <p className="text-sm text-[#52525B] leading-relaxed">
          Aus über 1.000 Live-Streams. Alle 8–20 Sekunden wird das Audio transkribiert und ins Englische übersetzt — direkt neben dem Original.
        </p>
        <div className="mt-6 flex items-center justify-center gap-3 overline">
          <span>WHISPER · DE</span>
          <span>→</span>
          <span className="text-[#002FA7]">GPT · EN</span>
        </div>
      </div>
    </div>
  );
}

function HistoryList({ history, onReload }) {
  if (!history.length) {
    return (
      <div className="p-6">
        <div className="overline mb-2">Verlauf leer</div>
        <div className="text-sm text-[#52525B]">Spiele einen Sender ab, um Transkripte zu sammeln.</div>
        <button onClick={onReload} className="mt-4 overline hover:text-[#002FA7]">↻ Neu laden</button>
      </div>
    );
  }
  return (
    <div>
      {history.map((h, i) => (
        <div key={h.id || i} className="border-b border-[#E4E4E7] px-4 py-3" data-testid={`history-item-${i}`}>
          <div className="flex items-center justify-between">
            <div className="overline truncate">{h.station_name || "—"}</div>
            <div className="font-mono-tech text-[0.65rem] text-[#A1A1AA]">{formatTime(h.timestamp)}</div>
          </div>
          <div className="text-xs mt-1 line-clamp-2 text-[#09090B]">{h.german}</div>
          <div className="text-xs mt-1 line-clamp-2 text-[#002FA7]">{h.english}</div>
        </div>
      ))}
    </div>
  );
}
